import React, { PropsWithChildren, useCallback, useMemo, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { BaseModalType, useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import { Modal } from '@ui/components/Modal'

import type { DxTradeExecutableSide } from '@model/DxTrade.types'
import { CurrencyType } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { getStepDecimals } from '../../utils/helpers'
import { LivePriceCell } from '../components/LivePriceCell'
import { TPSLButton } from '../components/TPSLButton'
import { TradePnL } from '../components/TradePnL'
import { useDxtradeAccountMetricsWS } from '../context/DxtradeAccountMetricsContext'
import {
  getPortfolioStoreSnapshot,
  subscribeToPortfolioStore,
} from '../hooks/useDxtradeAccountWebSocket'
import type { PortfolioTrade } from '../hooks/useDxtradePortfolioTrades'
import type { UseFixTradingWebSocketResult } from '../hooks/useFixTradingWebSocket'
import { usePositionCashAdjustments } from '../hooks/usePositionCashAdjustments'
import { useTradePnl } from '../hooks/useTradePnl'
import {
  buildTradeWidgetUrl,
  formatCurrency,
  formatDate,
  formatSymbol,
  renderPnl,
  renderSide,
} from '../utils/formatters'
import { findTradeProtectiveOrders } from '../utils/protectiveOrders'
import { CloseTradeModal } from './CloseTradeModal'
import { TPSLModal } from './TPSLModal'

import styles from './TradeDetailsOpenModal.module.scss'

type TradeDetailsModalProps = {
  trade: PortfolioTrade
  accountCurrency?: CurrencyType
  newOrder: UseFixTradingWebSocketResult['newOrder']
  closePosition: UseFixTradingWebSocketResult['closePosition']
  cancelOrder?: UseFixTradingWebSocketResult['cancelOrder']
  replaceOrder: UseFixTradingWebSocketResult['replaceOrder']
  accountCode?: string
  instrumentMeta: { pipSize?: number | null; priceIncrement?: number | null }
}

export const TradeDetailsOpenModal: BaseModalType<TradeDetailsModalProps> = (props) => {
  const {
    close,
    trade,
    accountCurrency,
    newOrder,
    closePosition,
    accountCode,
    cancelOrder,
    replaceOrder,
  } = props
  const { t } = useTranslation()
  const { formatNumber } = useFormatNumber()
  const navigate = useNavigate()
  const fractionDigits = getStepDecimals(props.instrumentMeta.priceIncrement)

  const handleNavigateToWidget = useCallback(() => {
    navigate(buildTradeWidgetUrl(trade.symbol, (trade.side ?? 'BUY') as DxTradeExecutableSide))
    close()
  }, [navigate, trade.symbol, trade.side, close])
  const closeTradeModal = useModal(CloseTradeModal, { closePosition })
  const tpslModal = useModal(TPSLModal)

  const wsAccountKey = accountCode ? `default:${accountCode}` : ''
  const wsMetrics = useDxtradeAccountMetricsWS(wsAccountKey)
  const liveMargin =
    wsMetrics?.positions?.find((p) => p.positionCode === trade.tradeId)?.margin ?? trade.margin

  const subscribePortfolio = useCallback(
    (listener: () => void) => subscribeToPortfolioStore(accountCode ?? '', listener),
    [accountCode]
  )
  const wsPortfolio = useSyncExternalStore(subscribePortfolio, () =>
    getPortfolioStoreSnapshot(accountCode ?? '')
  )

  const wsProtectiveOrders = useMemo(
    () => findTradeProtectiveOrders(trade.tradeId, wsPortfolio?.orders),
    [wsPortfolio?.orders, trade.tradeId]
  )

  const currentTrade = useMemo<PortfolioTrade>(
    () => (wsPortfolio ? { ...trade, ...wsProtectiveOrders } : trade),
    [trade, wsPortfolio, wsProtectiveOrders]
  )

  const pnl = useTradePnl(trade, accountCurrency)

  const { commission, overnightFees } = usePositionCashAdjustments(accountCode ?? '', trade.tradeId)

  const netPnl =
    pnl != null ? pnl + (commission?.total ?? 0) + (overnightFees?.total ?? 0) : undefined

  return (
    <Modal>
      <Modal.Header canClose>{t('Portfolio.Trade details')}</Modal.Header>
      <Modal.Body>
        <Box fullWidth>
          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            mb='x16'
            radius='x16'
            border='outline-low-em'
            onClick={handleNavigateToWidget}
          >
            <Box flex justify='space-between'>
              <Body1 color='text-high-em' medium>
                {formatSymbol(trade.symbol, trade.description)}
              </Body1>
              <TradePnL
                trade={trade}
                accountCurrency={accountCurrency}
                Typography={(typographyProps) => <Body1 medium {...typographyProps} />}
              />
            </Box>
            <Box flex justify='space-between'>
              <Label>
                <Caption1>
                  {renderSide(trade.side, Caption1)} <SensitiveData>{trade.sizeLots}</SensitiveData>{' '}
                  {t('at')}{' '}
                  <span>
                    {trade.entryPrice != null ? (
                      <SensitiveData>
                        {formatNumber(trade.entryPrice, {
                          minDecimalPlaces: fractionDigits,
                          maxDecimalPlaces: fractionDigits,
                        })}
                      </SensitiveData>
                    ) : (
                      '-'
                    )}
                  </span>
                </Caption1>
              </Label>

              <SensitiveData>
                <LivePriceCell
                  trade={{
                    symbol: trade.symbol,
                    side: trade.side,
                    priceIncrement: props.instrumentMeta.priceIncrement,
                  }}
                  TypographyComp={Caption1}
                />
              </SensitiveData>
            </Box>
          </Box>

          <Box flex direction='column' mb='x16'>
            <TPSLButton
              onPress={() => {
                tpslModal.open({
                  position: currentTrade,
                  accountCode,
                  accountCurrency,
                  instrumentCurrency: (currentTrade.instrumentCurrency ?? undefined) as
                    | CurrencyType
                    | undefined,
                  instrumentMeta: props.instrumentMeta,
                  cancelOrder: cancelOrder,
                  newOrder,
                  replaceOrder,
                  onTPSLChange: close,
                })
              }}
            />
          </Box>

          <Box
            flex
            direction='column'
            px='x16'
            py='x16'
            mb='x16'
            radius='x16'
            border='outline-low-em'
          >
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.TradeID')}</Label>
              <Value>{trade.tradeId}</Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.OpenedDate')}</Label>
              <Value>{formatDate(trade.openedOn)}</Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.Size')}</Label>
              <Value>
                <SensitiveData>{trade.sizeLots}</SensitiveData>
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.EntryPrice')}</Label>
              <Value>
                {trade.entryPrice != null ? (
                  <SensitiveData>
                    {formatNumber(trade.entryPrice, {
                      minDecimalPlaces: fractionDigits,
                      maxDecimalPlaces: fractionDigits,
                    })}
                  </SensitiveData>
                ) : (
                  '-'
                )}
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.CurrentPrice')}</Label>

              <LivePriceCell
                trade={{
                  symbol: trade.symbol,
                  side: trade.side,
                  priceIncrement: props.instrumentMeta.priceIncrement,
                }}
                TypographyComp={Body2}
              />
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.Margin')}</Label>
              <Value>
                <SensitiveData>{formatCurrency(liveMargin, accountCurrency)}</SensitiveData>
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.StopLoss')}</Label>
              <Value>
                {currentTrade.stopLoss != null ? (
                  <SensitiveData>
                    {formatNumber(currentTrade.stopLoss, {
                      minDecimalPlaces: fractionDigits,
                      maxDecimalPlaces: fractionDigits,
                    })}
                  </SensitiveData>
                ) : (
                  '—'
                )}
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.TakeProfit')}</Label>
              <Value>
                {currentTrade.takeProfit != null ? (
                  <SensitiveData>
                    {formatNumber(currentTrade.takeProfit, {
                      minDecimalPlaces: fractionDigits,
                      maxDecimalPlaces: fractionDigits,
                    })}
                  </SensitiveData>
                ) : (
                  '—'
                )}
              </Value>
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.Profit')}</Label>
              <TradePnL trade={trade} accountCurrency={accountCurrency} Typography={Body2} />
            </Box>
            <Box flex justify='space-between' mb='x8'>
              <Label>{t('Portfolio.NetP&L')}</Label>
              <SensitiveData>
                {renderPnl(netPnl, Body2, accountCurrency, undefined, styles.numericValue)}
              </SensitiveData>
            </Box>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Primary
          onClick={() => {
            close()
            closeTradeModal.open({ trade: currentTrade, accountCurrency, accountCode })
          }}
        >
          {t('Portfolio.Close Trade')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}

const Label: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-med-em'>{children}</Body2>
)

const Value: React.FC<PropsWithChildren> = ({ children }) => (
  <Body2 color='text-high-em'>{children}</Body2>
)
